import { create } from 'zustand'
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, type User, type UserCredential } from 'firebase/auth'
import { auth } from '../lib/firebase'
import { signInWithGoogleCredential, signInWithGooglePopup } from '../lib/googleAuth'
import { api } from '../services/api'
import { getDeviceId } from '../lib/deviceId'
import { resetStoreDeviceApprovalGate } from '../hooks/useStoreDeviceApprovalGate'

/**
 * #867: 店舗ログイン成功後に device を記録(best-effort).
 * 初回 device は bootstrap で trusted=True、以降は trusted=False で承認 gate 対象。
 * Firebase 認証経路でのみ成立(token 必須)。失敗は握り潰す(login を壊さない)。
 *
 * #869c: Firebase MFA で TOTP 強要素を直前に通過した sign-in なら
 * `viaTotp=true` を渡し backend が auto-trust する(WebAuthn refined-(a) 相当)。
 * ID token claim `signInSecondFactor` を見て自動判定。
 */
function registerStoreDeviceBestEffort(): void {
  try {
    void (async () => {
      let viaTotp = false
      try {
        const tokenResult = await auth.currentUser?.getIdTokenResult()
        if (tokenResult?.signInSecondFactor === 'totp') {
          viaTotp = true
        }
      } catch { /* best-effort */ }
      api.storeRegisterDevice(getDeviceId(), navigator.userAgent.slice(0, 80), viaTotp)
        .catch(() => {})
    })()
  } catch {
    /* device-id 取得失敗等は無視 */
  }
}

/**
 * Store owner derived status (legacy backward-compat field).
 *
 * Since #242 this is derived from `stripeCapabilities` on the backend:
 * - `unset`: Stripe Connect account not yet created
 * - `pending`: account exists but charges_enabled=false
 * - `approved`: charges_enabled=true (payouts_enabled may still be false — the
 *   UI surfaces a "payouts pending" banner rather than blocking access)
 * - `rejected`: Stripe returned `disabled_reason` starting with `rejected.*`
 *
 * Prefer reading `stripeCapabilities` directly for banner / CTA logic.
 */
export type OwnerStatus = 'unset' | 'pending' | 'approved' | 'rejected'

/**
 * Raw Stripe Connect capability snapshot (#242).
 *
 * Mirrors the flags Stripe exposes on an Account object. Backend returns these
 * directly from Stripe (falling back to the Firestore cache on error) so the
 * frontend can render action-required / payouts-pending / verification-pending
 * banners independently, following Stripe's recommended pattern.
 */
export interface StripeCapabilities {
  chargesEnabled: boolean | null
  payoutsEnabled: boolean | null
  detailsSubmitted: boolean | null
  disabledReason: string | null
  requirementsCurrentlyDue: string[]
  requirementsPastDue: string[]
  requirementsPendingVerification: string[]
}

interface StoreOwnerState {
  firebaseUser: User | null
  isStoreLoggedIn: boolean
  loading: boolean
  ownerName: string
  companyName: string
  // #959 (管理番号_009/010) 決済音 (per-owner)。'' は「オフ」。オーナーの
  // 全店舗の会計完了時に鳴る。/store-auth/me から populate。
  paymentSoundId: string
  // #962 (管理番号_009) 店舗サウンドの master 音量 (0–1)。注文音・提供音に適用。
  // per-owner・全店共通。/store-auth/me から populate（未設定は 0.5）。
  storeSoundVolume: number
  ownedStores: Record<string, unknown>[]
  ownerStatus: OwnerStatus
  /** #1431 本人確認をせず「現金のみ」で始めたオーナーか。
   *  店舗画面のゲート (RequireApprovedOwner) がこれを見る。 */
  cashOnlyOwner: boolean
  /** ownerStatus の初回 fetch がまだ完了していない間 true。`ownerStatus` の
   *  初期値が 'unset' (= 「未連携」と同じ値) なので、これが無いと
   *  RequireApprovedOwner が fetch 完了前に 'unset' を見て /store/onboarding
   *  へ誤 redirect する race condition を踏む。 */
  ownerStatusLoading: boolean
  stripeCapabilities: StripeCapabilities | null

  storeRegister: (email: string, password: string, ownerName?: string, companyName?: string) => Promise<void>
  storeLogin: (email: string, password: string) => Promise<void>
  /**
   * Sign in / register a store owner via Google.
   *
   * - Pass `gisIdToken` when the caller already obtained a JWT from Google
   *   Identity Services (FedCM flow, post-#496). We exchange it via Firebase
   *   `signInWithCredential` so the user record is created identically to
   *   the legacy popup flow.
   * - Omit it (or call from the emulator) to fall back to `signInWithPopup`,
   *   which is what the Firebase Auth Emulator supports for local dev.
   */
  storeLoginWithGoogle: (gisIdToken?: string) => Promise<{ stores: Record<string, unknown>[] }>
  /**
   * TOTP 2要素 (#840 opt-in) を enroll 済オーナーのログイン時、Firebase は
   * `auth/multi-factor-auth-required` を投げる。StoreLogin がそれを
   * `TotpSignInModal` で解決すると `resolveSignIn` の UserCredential が得られる。
   * storeLogin/storeLoginWithGoogle は credential 取得前に throw しているので、
   * 解決後にここで firebaseUser/isStoreLoggedIn を確定させる
   * (= 通常ログインの set(...) 相当)。
   */
  markStoreSignedIn: (user: User) => void
  storeLogout: () => Promise<void>
  deleteStoreOwner: () => Promise<void>
  fetchOwnedStores: () => Promise<void>
  fetchOwnerStatus: () => Promise<OwnerStatus>
  /** #1431 本人確認をせず現金のみで始める / やめる。 */
  startCashOnly: () => Promise<void>
  stopCashOnly: () => Promise<void>
  updateProfile: (ownerName: string, companyName: string) => Promise<void>
  // #959 決済音を per-owner で保存（'' でオフ）。全店舗に適用。
  updatePaymentSound: (soundId: string) => Promise<void>
  // #962 店舗サウンド音量 (0–1) を per-owner で保存。注文音・提供音に適用。
  updateStoreSoundVolume: (volume: number) => Promise<void>
  getIdToken: () => Promise<string | null>
  // Force a fresh ID token so newly-set Firebase Auth custom claims (e.g.
  // storeIds from #192) become visible to rules. Call after a store is
  // created or deleted, before any subsequent Storage write.
  refreshIdToken: () => Promise<string | null>
  // Primarily used by tests / mock flows; production code should call fetchOwnerStatus.
  setOwnerStatus: (status: OwnerStatus) => void
}

const EMPTY_CAPS: StripeCapabilities = {
  chargesEnabled: null,
  payoutsEnabled: null,
  detailsSubmitted: null,
  disabledReason: null,
  requirementsCurrentlyDue: [],
  requirementsPastDue: [],
  requirementsPendingVerification: [],
}

function normalizeCaps(data: Record<string, unknown> | null | undefined): StripeCapabilities {
  if (!data) return { ...EMPTY_CAPS }
  const asArray = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
  return {
    chargesEnabled: typeof data.chargesEnabled === 'boolean' ? data.chargesEnabled : null,
    payoutsEnabled: typeof data.payoutsEnabled === 'boolean' ? data.payoutsEnabled : null,
    detailsSubmitted: typeof data.detailsSubmitted === 'boolean' ? data.detailsSubmitted : null,
    disabledReason: typeof data.disabledReason === 'string' ? data.disabledReason : null,
    requirementsCurrentlyDue: asArray(data.requirementsCurrentlyDue),
    requirementsPastDue: asArray(data.requirementsPastDue),
    requirementsPendingVerification: asArray(data.requirementsPendingVerification),
  }
}

export const useStoreOwner = create<StoreOwnerState>((set, get) => ({
  firebaseUser: null,
  isStoreLoggedIn: false,
  loading: true,
  ownerName: '',
  companyName: '',
  paymentSoundId: '',
  storeSoundVolume: 0.5,
  ownedStores: [],
  ownerStatus: 'unset',
  cashOnlyOwner: false,
  // 初回 fetchOwnerStatus 完了まで RequireApprovedOwner を待たせる。
  // ログイン済みで status fetch がまだなら true、未ログイン or fetch 完了で false。
  ownerStatusLoading: true,
  stripeCapabilities: null,

  storeRegister: async (email: string, password: string, ownerName?: string, companyName?: string) => {
    // backend が email+password の Firebase Auth user を作る (`firebase_auth.create_user`)。
    // 旧実装は backend から発行された custom token で signInWithCustomToken していたが、
    // それだと Firebase Auth が first factor を "custom" 扱いし、後で TOTP (MFA) を
    // enroll しようとすると `auth/unsupported-first-factor` で必ず失敗する (#846)。
    // 同じ email/password で signInWithEmailAndPassword し直して first factor を
    // "password" にすると MFA enroll 可能。backend の custom_token return は
    // 後方互換のためそのまま無視。
    await api.storeAuthRegister(email, password, ownerName, companyName)
    const credential = await signInWithEmailAndPassword(auth, email, password)
    set({ firebaseUser: credential.user, isStoreLoggedIn: true, ownerName: ownerName || '', companyName: companyName || '' })
  },

  storeLogin: async (email: string, password: string) => {
    // TOTP enroll 済の場合 signInWithEmailAndPassword は
    // `auth/multi-factor-auth-required` を throw する。ここでは catch せず
    // 呼び元 (StoreLogin) に伝播させ、TotpSignInModal → markStoreSignedIn で
    // 合流する (#840)。
    const credential = await signInWithEmailAndPassword(auth, email, password)
    set({ firebaseUser: credential.user, isStoreLoggedIn: true })
    // #867: device-approval gate の前提。best-effort、login を壊さない。
    registerStoreDeviceBestEffort()
  },

  markStoreSignedIn: (user: User) => {
    set({ firebaseUser: user, isStoreLoggedIn: true })
    // #867: TOTP MFA challenge 経由のログイン成功時にも device 記録。
    registerStoreDeviceBestEffort()
  },

  storeLoginWithGoogle: async (gisIdToken?: string) => {
    const credential: UserCredential = gisIdToken
      ? await signInWithGoogleCredential(gisIdToken)
      : await signInWithGooglePopup()
    const token = await credential.user.getIdToken()
    const data = await api.storeAuthGoogleInit(token)
    set({
      firebaseUser: credential.user,
      isStoreLoggedIn: true,
      ownerName: data.ownerName || '',
      companyName: data.companyName || '',
      paymentSoundId: data.paymentSoundId || '',
      storeSoundVolume: typeof data.storeSoundVolume === 'number' ? data.storeSoundVolume : 0.5,
      ownedStores: data.stores || [],
    })
    // #867: device-approval gate の前提。best-effort。
    registerStoreDeviceBestEffort()
    return { stores: data.stores || [] }
  },

  storeLogout: async () => {
    await signOut(auth)
    // #867: gate cache を別 user 評価のためクリア。
    resetStoreDeviceApprovalGate()
    set({ firebaseUser: null, isStoreLoggedIn: false, ownedStores: [], ownerName: '', companyName: '', paymentSoundId: '', storeSoundVolume: 0.5, ownerStatus: 'unset', cashOnlyOwner: false, ownerStatusLoading: false, stripeCapabilities: null })
  },

  deleteStoreOwner: async () => {
    const token = await get().getIdToken()
    if (!token) throw new Error('Not authenticated')
    await api.storeAuthDeleteAccount(token)
    await signOut(auth)
    set({ firebaseUser: null, isStoreLoggedIn: false, ownedStores: [], ownerName: '', companyName: '', paymentSoundId: '', storeSoundVolume: 0.5, ownerStatus: 'unset', cashOnlyOwner: false, ownerStatusLoading: false, stripeCapabilities: null })
  },

  fetchOwnedStores: async () => {
    const token = await get().getIdToken()
    if (!token) return
    try {
      const data = await api.storeAuthMe(token)
      set({
        ownedStores: data.stores || [],
        ownerName: data.ownerName || '',
        companyName: data.companyName || '',
        paymentSoundId: data.paymentSoundId || '',
        storeSoundVolume: typeof data.storeSoundVolume === 'number' ? data.storeSoundVolume : 0.5,
      })
    } catch {
      set({ ownedStores: [] })
    }
  },

  fetchOwnerStatus: async () => {
    set({ ownerStatusLoading: true })
    const token = await get().getIdToken()
    if (!token) {
      set({ ownerStatus: 'unset', cashOnlyOwner: false, stripeCapabilities: null, ownerStatusLoading: false })
      return 'unset'
    }
    try {
      const data = await api.stripeConnectStatus(token)
      const raw = (data?.status as OwnerStatus | undefined) ?? 'unset'
      const valid: OwnerStatus[] = ['unset', 'pending', 'approved', 'rejected']
      const status: OwnerStatus = valid.includes(raw) ? raw : 'unset'
      set({
        ownerStatus: status,
        cashOnlyOwner: (data as { cashOnlyOwner?: boolean })?.cashOnlyOwner === true,
        stripeCapabilities: normalizeCaps(data as Record<string, unknown>),
        ownerStatusLoading: false,
      })
      return status
    } catch {
      set({ ownerStatus: 'unset', cashOnlyOwner: false, stripeCapabilities: null, ownerStatusLoading: false })
      return 'unset'
    }
  },

  startCashOnly: async () => {
    const token = await get().getIdToken()
    if (!token) return
    await api.startCashOnlyOwner(token)
    set({ cashOnlyOwner: true })
  },

  stopCashOnly: async () => {
    const token = await get().getIdToken()
    if (!token) return
    await api.stopCashOnlyOwner(token)
    set({ cashOnlyOwner: false })
  },

  updateProfile: async (ownerName: string, companyName: string) => {
    const token = await get().getIdToken()
    if (!token) return
    await api.storeAuthUpdateProfile(token, ownerName, companyName)
    set({ ownerName, companyName })
  },

  // #959 決済音 (per-owner) を保存。'' でオフ。成功後にローカル state も更新して
  // StorePreferences の選択と StoreOrders の再生に即反映する。
  updatePaymentSound: async (soundId: string) => {
    const token = await get().getIdToken()
    if (!token) throw new Error('Not authenticated')
    await api.storeAuthUpdatePaymentSound(token, soundId)
    set({ paymentSoundId: soundId })
  },

  // #962 店舗サウンド音量 (0–1) を per-owner で保存。先にローカル state を更新して
  // スライダー/テスト再生が即反映するようにし、その後サーバへ送る。
  updateStoreSoundVolume: async (volume: number) => {
    const vol = Math.max(0, Math.min(1, volume))
    set({ storeSoundVolume: vol })
    const token = await get().getIdToken()
    if (!token) throw new Error('Not authenticated')
    await api.storeAuthUpdateSoundVolume(token, vol)
  },

  getIdToken: async () => {
    const user = get().firebaseUser
    if (!user) return null
    return user.getIdToken()
  },

  refreshIdToken: async () => {
    const user = get().firebaseUser
    if (!user) return null
    // forceRefresh=true triggers an IdentityToolkit call that returns a token
    // containing the latest custom claims (#192).
    return user.getIdToken(true)
  },

  setOwnerStatus: (status: OwnerStatus) => set({ ownerStatus: status }),
}))

// Listen for auth state changes and keep ownerStatus aligned with backend.
onAuthStateChanged(auth, (user) => {
  useStoreOwner.setState({
    firebaseUser: user,
    isStoreLoggedIn: !!user,
    loading: false,
    // user 有: fetchOwnerStatus が走るまでは "loading" 扱い (race 防止)
    // user 無: 待つ必要が無いので false
    ownerStatusLoading: !!user,
  })
  if (user) {
    // fetchOwnerStatus 内で ownerStatusLoading を false に倒す。失敗時も
    // catch ブロックで false に倒すので RequireApprovedOwner が永久 spinner
    // になることはない (failures keep ownerStatus='unset' → /store/onboarding)。
    useStoreOwner.getState().fetchOwnerStatus().catch(() => {})
  } else {
    useStoreOwner.setState({ ownerStatus: 'unset', stripeCapabilities: null })
  }
})
